// @ts-nocheck
// Reviewer-invitations management:
// - Lists all invitations (newest first)
// - Create-new modal calls admin_create_invitation RPC and shows the
//   freshly-minted personal link with a copy button so the author can
//   forward it via their own email tool
// - Search filters by name/email
// - Status pill: ניצל / נפתח / טרם נפתח (claimed / first-opened / untouched)

import { useEffect, useMemo, useState } from "react";
import { supabase } from "../../lib/supabase";
import { useTableSort, SortableTH } from "./useTableSort";

type Invitation = {
  id: string;
  token: string;
  email: string;
  name: string;
  letter_md: string | null;
  created_at: string;
  claimed_at: string | null;
  first_opened_at?: string | null;
  kind?: "reviewer" | "gift";
};

const ORIGIN =
  typeof window !== "undefined" ? window.location.origin : "https://eiforall.com";

// Strip Hebrew letters + bidirectional control marks from email input so
// stray paste content like "דוד כהן david@example.com" cleans up to just
// the email part. Latin chars / numbers / @ . _ - all pass through.
function stripHebrewAndBidi(value: string): string {
  return value.replace(/[֐-׿‎‏]/g, "").trim();
}

export function AdminInvitations() {
  const [rows, setRows] = useState<Invitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from("reviewer_invitations")
        .select("*")
        .order("created_at", { ascending: false });
      if (cancelled) return;
      if (error) setError(error.message);
      else setRows(data || []);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [refresh]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        r.name.toLowerCase().includes(q) ||
        r.email.toLowerCase().includes(q)
    );
  }, [rows, query]);

  const { sortBy, sortIndicator, sortedRows } = useTableSort(filtered, {
    kind:       (r) => r.kind || "reviewer",
    name:       (r) => r.name,
    email:      (r) => r.email,
    // Status sort: untouched first, then opened-not-claimed, then claimed.
    status:     (r) => (r.claimed_at ? 2 : r.first_opened_at ? 1 : 0),
    opened_at:  (r) => r.first_opened_at || "",
    created_at: (r) => r.created_at,
  });

  return (
    <>
      <div className="adm-toolbar">
        <input
          className="adm-search"
          placeholder="חיפוש לפי שם או מייל…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <button className="adm-btn" onClick={() => setShowNew(true)}>
          + הזמנה חדשה
        </button>
        <button className="adm-btn secondary" onClick={() => setShowBulk(true)}>
          + מתנות בכמות
        </button>
      </div>

      {error && <div className="adm-error">{error}</div>}

      <div className="adm-card" style={{ padding: 0, overflow: "hidden" }}>
        {loading ? (
          <div className="adm-loading">טוען הזמנות…</div>
        ) : filtered.length === 0 ? (
          <div className="adm-empty">
            אין הזמנות{query ? " תואמות" : ""}.
            {!query && " לחצו על 'הזמנה חדשה' כדי להתחיל."}
          </div>
        ) : (
          <table className="adm-table">
            <thead>
              <tr>
                <SortableTH k="kind" sortBy={sortBy} sortIndicator={sortIndicator}>סוג</SortableTH>
                <SortableTH k="name" sortBy={sortBy} sortIndicator={sortIndicator}>שם</SortableTH>
                <SortableTH k="email" sortBy={sortBy} sortIndicator={sortIndicator}>מייל</SortableTH>
                <SortableTH k="status" sortBy={sortBy} sortIndicator={sortIndicator}>סטטוס</SortableTH>
                <SortableTH k="opened_at" sortBy={sortBy} sortIndicator={sortIndicator}>נפתח לראשונה</SortableTH>
                <SortableTH k="created_at" sortBy={sortBy} sortIndicator={sortIndicator}>נשלח</SortableTH>
                <th>קישור אישי</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {sortedRows.map((r) => (
                <tr key={r.id}>
                  <td>
                    {r.kind === "gift" ? (
                      <span className="adm-pill">מתנה</span>
                    ) : (
                      <span className="adm-pill">סקירה</span>
                    )}
                  </td>
                  <td>{r.name}</td>
                  <td className="mono">{r.email}</td>
                  <td>
                    {r.claimed_at ? (
                      <span className="adm-pill ok">ניצל</span>
                    ) : r.first_opened_at ? (
                      <span className="adm-pill">נפתח, טרם נרשם</span>
                    ) : (
                      <span className="adm-pill pending">טרם נפתח</span>
                    )}
                  </td>
                  <td className="num">
                    {r.first_opened_at
                      ? new Date(r.first_opened_at).toLocaleString("he-IL", {
                          day: "numeric",
                          month: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })
                      : "—"}
                  </td>
                  <td className="num">
                    {new Date(r.created_at).toLocaleDateString("he-IL")}
                  </td>
                  <td>
                    <CopyButton link={`${ORIGIN}/r/${r.token}`} />
                  </td>
                  <td>
                    <DeleteButton
                      row={r}
                      onDeleted={() => setRefresh((n) => n + 1)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showNew && (
        <NewInvitationModal
          onClose={() => setShowNew(false)}
          onCreated={() => {
            setShowNew(false);
            setRefresh((n) => n + 1);
          }}
        />
      )}

      {showBulk && (
        <BulkGiftsModal
          onClose={() => setShowBulk(false)}
          onCreated={() => {
            setShowBulk(false);
            setRefresh((n) => n + 1);
          }}
        />
      )}
    </>
  );
}

function DeleteButton({
  row,
  onDeleted,
}: {
  row: Invitation;
  onDeleted: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const onClick = async () => {
    const claimedWarning = row.claimed_at
      ? "ההזמנה כבר נוצלה — מחיקה תבטל את הגישה של הקורא. להמשיך?"
      : `למחוק את ההזמנה של ${row.name || row.email}?`;
    if (!window.confirm(claimedWarning)) return;
    setBusy(true);
    const { error } = await supabase.rpc("admin_delete_invitation", {
      _id: row.id,
    });
    setBusy(false);
    if (error) {
      window.alert("שגיאה במחיקה: " + error.message);
      return;
    }
    onDeleted();
  };
  return (
    <button
      className="adm-btn secondary tiny"
      onClick={onClick}
      disabled={busy}
      title="מחיקת ההזמנה"
    >
      {busy ? "מוחק…" : "מחק"}
    </button>
  );
}

function CopyButton({ link }: { link: string }) {
  const [copied, setCopied] = useState(false);
  const onClick = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {}
  };
  return (
    <button className="adm-btn secondary tiny" onClick={onClick}>
      {copied ? "הועתק ✓" : "העתק קישור"}
    </button>
  );
}

function NewInvitationModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [letter, setLetter] = useState("");
  const [status, setStatus] = useState<"idle" | "saving" | "done" | "error">(
    "idle"
  );
  const [errorMsg, setErrorMsg] = useState("");
  const [link, setLink] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus("saving");
    setErrorMsg("");
    const { data, error } = await supabase.rpc("admin_create_invitation", {
      _name: name.trim(),
      _email: email.trim(),
      _letter: letter.trim() || null,
    });
    if (error) {
      setErrorMsg(error.message);
      setStatus("error");
      return;
    }
    const row = Array.isArray(data) ? data[0] : data;
    setLink(row?.personal_link || "");
    setStatus("done");
  };

  return (
    <div className="adm-modal-overlay" onClick={onClose}>
      <div className="adm-modal" onClick={(e) => e.stopPropagation()}>
        <button className="adm-modal-close" onClick={onClose}>
          ×
        </button>
        <h3 style={{ fontFamily: "'Frank Ruhl Libre', serif", fontSize: 22, color: "#5C3D2E", marginBottom: 6 }}>
          הזמנה חדשה לסקירה
        </h3>
        <p style={{ color: "#8B7355", fontSize: 13, marginBottom: 18 }}>
          הקישור שיופק יישלח על ידכם — פתחו את אפליקציית המייל ושלחו אותו עם
          ברכה אישית.
        </p>

        {status === "done" ? (
          <div>
            <div className="adm-pill ok" style={{ marginBottom: 14 }}>
              נוצר בהצלחה
            </div>
            <p style={{ marginBottom: 12 }}>
              הקישור האישי של {name || "הקורא"}:
            </p>
            <div className="adm-link-row">
              <code>{link}</code>
              <CopyButton link={link} />
            </div>
            <div style={{ display: "flex", gap: 10, marginTop: 18 }}>
              <button className="adm-btn" onClick={onCreated}>
                סיום
              </button>
            </div>
          </div>
        ) : (
          <form className="adm-form" onSubmit={handleSubmit} autoComplete="off">
            <div>
              <label htmlFor="inv-name">שם הקורא</label>
              <input
                id="inv-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                placeholder="דוד כהן"
                autoComplete="off"
                name="invitation-recipient-name"
              />
            </div>
            <div>
              <label htmlFor="inv-email">מייל</label>
              <input
                id="inv-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(stripHebrewAndBidi(e.target.value))}
                required
                placeholder="david@example.com"
                autoComplete="off"
                name="invitation-recipient-email"
                dir="ltr"
              />
            </div>
            <div>
              <label htmlFor="inv-letter">
                מכתב אישי (אופציונלי — אחרת ישתמש בברירת המחדל)
              </label>
              <textarea
                id="inv-letter"
                value={letter}
                onChange={(e) => setLetter(e.target.value)}
                placeholder="השתמשו ב‑{{name}} כדי שהמערכת תחליף בשם הקורא"
              />
            </div>
            {errorMsg && <div className="adm-error">{errorMsg}</div>}
            <div style={{ display: "flex", gap: 10 }}>
              <button
                type="submit"
                className="adm-btn"
                disabled={status === "saving"}
              >
                {status === "saving" ? "יוצר…" : "צור הזמנה והפק קישור"}
              </button>
              <button
                type="button"
                className="adm-btn secondary"
                onClick={onClose}
              >
                ביטול
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// BulkGiftsModal — paste a list of emails, get one personal link per recipient.
// All entries are recorded as kind='gift' so they're easy to distinguish from
// reviewer invitations in the table above.
// =============================================================================
function BulkGiftsModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const [raw, setRaw] = useState("");
  const [status, setStatus] = useState<"idle" | "saving" | "done" | "error">(
    "idle"
  );
  const [errorMsg, setErrorMsg] = useState("");
  const [results, setResults] = useState<
    { email: string; personal_link: string }[]
  >([]);

  const parsedEmails = raw
    .split(/[\s,;]+/)
    .map((s) => s.trim().toLowerCase())
    .filter((s) => s && s.includes("@"));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (parsedEmails.length === 0) {
      setErrorMsg("הדביקו לפחות מייל אחד תקין");
      setStatus("error");
      return;
    }
    setStatus("saving");
    setErrorMsg("");
    const { data, error } = await supabase.rpc(
      "admin_create_gift_invitations",
      { _emails: parsedEmails }
    );
    if (error) {
      setErrorMsg(error.message);
      setStatus("error");
      return;
    }
    setResults(Array.isArray(data) ? data : []);
    setStatus("done");
  };

  const copyAll = async () => {
    const text = results
      .map((r) => `${r.email}\t${r.personal_link}`)
      .join("\n");
    try {
      await navigator.clipboard.writeText(text);
    } catch {}
  };

  return (
    <div className="adm-modal-overlay" onClick={onClose}>
      <div className="adm-modal" onClick={(e) => e.stopPropagation()}>
        <button className="adm-modal-close" onClick={onClose}>
          ×
        </button>
        <h3
          style={{
            fontFamily: "'Frank Ruhl Libre', serif",
            fontSize: 22,
            color: "#5C3D2E",
            marginBottom: 6,
          }}
        >
          מתנות בכמות
        </h3>
        <p style={{ color: "#8B7355", fontSize: 13, marginBottom: 18 }}>
          הדביקו רשימת מיילים — אחד לשורה, או מופרדים בפסיק / נקודה-פסיק. נייצר קישור אישי לכל אחד.
          הנמענים מקבלים גישה מלאה לספר ללא תשלום.
          <br />
          <strong>אפשר להדביק שמות לצד המיילים</strong> (למשל "דוד כהן david@example.com") — המערכת תוציא רק את המיילים, לא צריך לנקות ידנית.
        </p>

        {status === "done" ? (
          <div>
            <div className="adm-pill ok" style={{ marginBottom: 14 }}>
              נוצרו {results.length} מתנות
            </div>
            <p style={{ marginBottom: 12, fontSize: 13, color: "#5C3D2E" }}>
              העתיקו את הקישורים ושלחו לכל נמען בנפרד (וואטסאפ / מייל / וכו׳):
            </p>
            <div
              style={{
                maxHeight: 280,
                overflowY: "auto",
                border: "1px solid #E8DCC8",
                borderRadius: 8,
                padding: 8,
                marginBottom: 14,
                background: "#FFFDF9",
              }}
            >
              {results.map((r) => (
                <div
                  key={r.personal_link}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "6px 4px",
                    borderBottom: "1px solid #F2E8D8",
                    fontSize: 13,
                  }}
                >
                  <span className="mono" style={{ flex: "0 0 220px" }}>
                    {r.email}
                  </span>
                  <code
                    style={{
                      flex: 1,
                      fontSize: 11,
                      color: "#A0704A",
                      direction: "ltr",
                      textAlign: "left",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {r.personal_link}
                  </code>
                  <CopyButton link={r.personal_link} />
                </div>
              ))}
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button className="adm-btn secondary" onClick={copyAll}>
                העתק הכל (TSV)
              </button>
              <button className="adm-btn" onClick={onCreated}>
                סיום
              </button>
            </div>
          </div>
        ) : (
          <form className="adm-form" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="bulk-emails">מיילים</label>
              <textarea
                id="bulk-emails"
                value={raw}
                onChange={(e) => setRaw(e.target.value)}
                rows={8}
                placeholder={
                  "david@example.com\nרינה כהן rina@example.com\nnir@gmail.com"
                }
                style={{ fontFamily: "monospace", direction: "ltr" }}
              />
            </div>
            {parsedEmails.length > 0 && (
              <div
                style={{
                  background: "#F3EDE3",
                  border: "1px solid #E8DCC8",
                  borderRadius: 10,
                  padding: "10px 12px",
                  fontSize: 12,
                  color: "#5C3D2E",
                }}
              >
                <div style={{ marginBottom: 6, fontWeight: 600 }}>
                  זוהו {parsedEmails.length} מיילים — אלה ייווצרו:
                </div>
                <div
                  style={{
                    maxHeight: 110,
                    overflowY: "auto",
                    fontFamily: "monospace",
                    direction: "ltr",
                    textAlign: "left",
                    color: "#2D1B10",
                    lineHeight: 1.5,
                  }}
                >
                  {parsedEmails.map((em) => (
                    <div key={em}>{em}</div>
                  ))}
                </div>
              </div>
            )}
            {parsedEmails.length === 0 && raw.trim().length > 0 && (
              <p style={{ fontSize: 12, color: "#C62828", margin: 0 }}>
                לא זוהה אף מייל תקין. ודאו שיש @ בכל כתובת.
              </p>
            )}
            {errorMsg && <div className="adm-error">{errorMsg}</div>}
            <div style={{ display: "flex", gap: 10 }}>
              <button
                type="submit"
                className="adm-btn"
                disabled={status === "saving" || parsedEmails.length === 0}
              >
                {status === "saving"
                  ? "יוצר…"
                  : `צור ${parsedEmails.length} קישורים`}
              </button>
              <button
                type="button"
                className="adm-btn secondary"
                onClick={onClose}
              >
                ביטול
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
